import { http, fallback, createConfig } from "wagmi";
import { mainnet } from "wagmi/chains";
import { injected, coinbaseWallet, walletConnect } from "wagmi/connectors";

// The 01 curve and token are live on Ethereum mainnet. Reads use public,
// browser-CORS-enabled RPCs; set VITE_RPC_URL to prefer a dedicated endpoint
// (it is tried first). The viem default (eth.merkle.io) blocks browser CORS,
// so we never rely on it from the browser.
//
// These are tried in order by viem's `fallback` transport: when the active
// endpoint errors (timeout, 5xx, node down) reads roll over to the next one,
// so a single flaky provider no longer breaks the dapp. A contract revert is a
// valid response and does NOT trigger failover. All of these allow browser
// CORS.
const PUBLIC_RPCS = [
  "https://ethereum-rpc.publicnode.com",
  "https://eth.drpc.org",
  "https://1rpc.io/eth",
];
const RPC_URLS = [
  ...(import.meta.env.VITE_RPC_URL
    ? [import.meta.env.VITE_RPC_URL as string]
    : []),
  ...PUBLIC_RPCS,
].filter((url, i, arr) => arr.indexOf(url) === i);

// WalletConnect project id (a public client identifier, safe to ship in the
// bundle). When present, the WalletConnect connector is offered so mobile and
// other wallets can pair by QR code.
const WC_PROJECT_ID = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID as
  | string
  | undefined;

const connectors = [
  injected(),
  coinbaseWallet({ appName: "01", preference: "all" }),
  ...(WC_PROJECT_ID
    ? [
        walletConnect({
          projectId: WC_PROJECT_ID,
          showQrModal: true,
          metadata: {
            name: "01",
            description: "The 01 curve and token, live on Ethereum mainnet.",
            url: "https://01.exchange",
            icons: [],
          },
        }),
      ]
    : []),
];

export const config = createConfig({
  chains: [mainnet],
  connectors,
  transports: {
    // batch groups concurrent eth_calls into one HTTP request, so a set of
    // reads lands on a single backend node. On the load-balanced public RPC
    // that keeps notes, root, and balances at the same height; a dedicated
    // VITE_RPC_URL removes the lag entirely. fallback rolls over to the next
    // endpoint if the active one fails.
    [mainnet.id]: fallback(RPC_URLS.map((url) => http(url, { batch: true }))),
  },
});

export const ACTIVE_CHAIN = mainnet;

declare module "wagmi" {
  interface Register {
    config: typeof config;
  }
}
